import { defineStore } from 'pinia';
import authService from '@/services/authService';
import googleAuthService from '@/services/googleAuthService';
import appleAuthService from '@/services/appleAuthService';

export const useAuthStore = defineStore({
  id: 'auth',

  state: () => ({
    user: JSON.parse(localStorage.getItem('indiefy-user')) || null,
    isAuthenticated: !!localStorage.getItem('indiefy-user'),
    loading: false,
    initializing: true,
    error: null,
    requiresTwoFactor: false,
    captchaData: null,
  }),

  getters: {
    currentUser: (state) => state.user,
    isLoading: (state) => state.loading,
    isInitializing: (state) => state.initializing,
    authError: (state) => state.error,
    needsTwoFactor: (state) => state.requiresTwoFactor,
  },

  actions: {
    async getCaptcha() {
      this.loading = true;
      this.error = null;

      try {
        const captchaResponse = await authService.getCaptcha();
        this.captchaData = captchaResponse;
        return captchaResponse;
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to load captcha';
        return null;
      } finally {
        this.loading = false;
      }
    },

    async login(username, password, captcha, lang = 'en') {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        const response = await authService.login({
          username,
          password,
          captcha,
          lang,
        });

        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        if (window.$chatwoot) {
          window.$chatwoot.setUser(this.user._id, {
            email: this.user.email,
          });

          if (this.user.subscription) {
            window.$chatwoot.setCustomAttributes({
              pro: true,
            });

            window.$chatwoot.setLabel('pro');
          }
        }

        return { success: true };
      } catch (error) {
        this.error = error.response?.data?.name || 'Authentication failed';
        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async confirmTwoFactor(code) {
      this.loading = true;
      this.error = null;

      try {
        const response = await authService.confirmTwoFactor({ code });

        this.user = response;
        this.isAuthenticated = true;
        this.requiresTwoFactor = false;

        // Check if user is blocked after two-factor confirmation
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        return { success: true };
      } catch (error) {
        this.error = error.response?.data?.message || 'Two-factor authentication failed';
        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async register(userData) {
      this.loading = true;
      this.error = null;

      try {
        if (localStorage.getItem('referral')) {
          userData.referred_by_user = localStorage.getItem('referral');
          localStorage.removeItem('referral');
        }

        const response = await authService.register(userData);

        this.user = response;
        this.isAuthenticated = true;

        if (this.user.first_time) {
          window.gtag && window.gtag('event', 'Account Created', {
            event_category: 'Accounts',
            event_label: 'Account Created',
          });
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        return { success: true };
      } catch (error) {
        this.error = error.response?.data?.message || 'Registration failed';
        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      this.loading = true;

      try {
        await authService.logout();
        // Use the clearSession helper
        this.clearSession();
        return { success: true };
      } catch (error) {
        // Even if server logout fails, clear the local session
        this.clearSession();
        this.error = error.response?.data?.message || 'Logout failed';
        return { success: true }; // Still return success to ensure UI updates
      } finally {
        this.loading = false;
      }
    },

    // Asynchronous verification with the server
    async verifySession() {
      // Don't set loading state to avoid UI flickering
      try {
        const userData = await authService.getCurrentUser();
        // Update user data with latest from server
        if (userData) {
          const serialized = JSON.stringify(userData);
          // Skip reactive reassignment when the server returned the same
          // payload we already have cached. Reassigning `this.user` to a
          // new object reference on every page load needlessly fires every
          // watcher on `currentUser` across the app (theme enforcement,
          // Apple-relay modal check, navigation bar computeds, etc.),
          // which can manifest as a visible "blink" on initial load.
          const unchanged = serialized === localStorage.getItem('indiefy-user');

          // Check if user is blocked/banned/frozen
          if (this.isUserBlocked(userData)) {
            if (!unchanged) {
              this.user = userData; // Still set user data so we can show the reason
              localStorage.setItem('indiefy-user', serialized);
            }
            this.isAuthenticated = true;

            // Import router dynamically to avoid circular dependency
            const router = await import('@/router');
            router.default.push('/account/blocked');
            return false; // Return false to indicate blocked status
          }

          if (!unchanged) {
            this.user = userData;
            // Update localStorage with fresh data
            localStorage.setItem('indiefy-user', serialized);
          }
          this.isAuthenticated = true;

          if (window.$chatwoot) {
            window.$chatwoot.setUser(userData._id, {
              email: userData.email,
            });

            if (userData.subscription) {
              window.$chatwoot.setCustomAttributes({
                pro: true,
              });

              window.$chatwoot.setLabel('pro');
            }
          }

          return true;
        } else {
          console.warn('Session verification returned no user data');
          this.clearSession();
          return false;
        }
      } catch (apiError) {
        console.warn('Session verification API call failed:', apiError);
        // Only clear session for auth errors (401)
        if (apiError.response && apiError.response.status === 401) {
          this.clearSession();
        }
        return false;
      } finally {
        this.initializing = false;
      }
    },

    // Helper method to check if user is blocked
    isUserBlocked(user = null) {
      const userData = user || this.user;
      if (!userData) return false;

      // Check if freeze or banned properties exist and are truthy
      // Handle both string and number values
      const isFrozen = userData.freeze;
      const isBanned = userData.banned;
      const isBlocked = isFrozen || isBanned;

      return isBlocked;
    },

    clearSession() {
      // Helper method to clear user session state
      this.user = null;
      this.isAuthenticated = false;
      localStorage.removeItem('indiefy-user');
    },

    clearError() {
      this.error = null;
    },

    async getUserActivity(params) {
      this.loading = true;
      this.error = null;

      try {
        const activity = await authService.getUserActivity(params);
        return activity;
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to fetch user activity';
        return null;
      } finally {
        this.loading = false;
      }
    },

    async loginWithGoogle() {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        // Get Google user data
        const googleUserData = await googleAuthService.signInWithGoogle();

        // Add language and source
        const userData = {
          ...googleUserData,
          lang: this.getUserLanguage(),
          source: 'web',
        };

        if (localStorage.getItem('referral')) {
          userData.referred_by_user = localStorage.getItem('referral');
          localStorage.removeItem('referral');
        }

        if (localStorage.getItem('utm_campaign')) {
          userData.utm_campaign = localStorage.getItem('utm_campaign');
        }

        // Send to backend
        const response = await authService.googleLogin(userData);

        if (localStorage.getItem('utm_campaign')) {
          localStorage.removeItem('utm_campaign');
        }

        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        if (this.user.first_time) {
          window.gtag && window.gtag('event', 'Account Created', {
            event_category: 'Accounts',
            event_label: 'Account Created',
          });
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        window.zE && window.zE('webWidget', 'identify', {
          name: this.user.name || this.user.username,
          email: this.user.email,
        });

        if (window.$chatwoot) {
          window.$chatwoot.setUser(this.user._id, {
            email: this.user.email,
          });

          if (this.user.subscription) {
            window.$chatwoot.setCustomAttributes({
              pro: true,
            });

            window.$chatwoot.setLabel('pro');
          }
        }

        return { success: true };
      } catch (error) {
        console.error('Google login error:', error);

        // Handle specific error cases
        if (error.response?.data?.name === 'duplicated_email') {
          this.error = 'This email is already registered. Please try logging in instead.';
        } else if (error.response?.data?.name === 'deactivated_account') {
          this.error = 'This account has been deactivated. Please contact support.';
        } else {
          this.error = error.response?.data?.message || 'Google authentication failed';
        }

        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async loginWithApple() {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        // Get Apple user data
        const appleUserData = await appleAuthService.signInWithApple();

        // Add language and source
        const userData = {
          ...appleUserData,
          lang: this.getUserLanguage(),
          source: 'web',
        };

        if (localStorage.getItem('referral')) {
          userData.referred_by_user = localStorage.getItem('referral');
          localStorage.removeItem('referral');
        }

        if (localStorage.getItem('utm_campaign')) {
          userData.utm_campaign = localStorage.getItem('utm_campaign');
        }

        // Send to backend
        const response = await authService.appleLogin(userData);

        if (localStorage.getItem('utm_campaign')) {
          localStorage.removeItem('utm_campaign');
        }

        if (response.first_time) {
          window.gtag && window.gtag('event', 'Account Created', {
            event_category: 'Accounts',
            event_label: 'Account Created',
          });
        }

        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        window.zE && window.zE('webWidget', 'identify', {
          name: this.user.name || this.user.username,
          email: this.user.email,
        });

        if (window.$chatwoot) {
          window.$chatwoot.setUser(this.user._id, {
            email: this.user.email,
          });

          if (this.user.subscription) {
            window.$chatwoot.setCustomAttributes({
              pro: true,
            });

            window.$chatwoot.setLabel('pro');
          }
        }

        return { success: true };
      } catch (error) {
        console.error('Apple login error:', error);

        // Handle specific error cases
        if (error.response?.data?.name === 'duplicated_email') {
          this.error = 'This email is already registered. Please try logging in instead.';
        } else if (error.response?.data?.name === 'deactivated_account') {
          this.error = 'This account has been deactivated. Please contact support.';
        } else {
          this.error = error.response?.data?.message || 'Apple authentication failed';
        }

        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    // Helper to get user language
    getUserLanguage() {
      // Try to get from i18n if available
      if (window.i18n && window.i18n.global && window.i18n.global.locale) {
        return window.i18n.global.locale.value;
      }

      // Fallback to browser language
      return navigator.language.split('-')[0] || 'en';
    },

    async handleAppleCallback(callbackData) {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        // Send the authorization code to the backend
        const response = await authService.appleCallback(callbackData);

        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        return { success: true };
      } catch (error) {
        console.error('Apple callback error:', error);

        // Handle specific error cases
        if (error.response?.data?.name === 'duplicated_email') {
          this.error = 'This email is already registered. Please try logging in instead.';
        } else if (error.response?.data?.name === 'deactivated_account') {
          this.error = 'This account has been deactivated. Please contact support.';
        } else {
          this.error = error.response?.data?.message || 'Apple authentication failed';
        }

        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },
  },
});